/**
 * @snippet NavigationDrawer
 * @description Light-theme sidebar with collapsible service groups.
 *
 *   Visual change from the previous dark variant:
 *     - White background, slate text. Brand orange reserved for the
 *       active item, the "+ New" CTA and the live-systems pulse.
 *     - Each `section` from the dynamic catalog renders as a collapsible
 *       group. The section containing the current route is auto-
 *       expanded; the rest start collapsed when the user has more
 *       than ~3 sections (keeps the rail scannable).
 *     - Mobile drawer behaviour preserved (slide-in over backdrop;
 *       focus trap stays active when isOpen).
 *     - Each nav row carries a proper icon (inline SVG). Collapsed
 *       mode shows the icon-only rail; expanded shows icon + label.
 *       Icons are stroke-based and sized to 18px so they read the
 *       same in both modes. Items the icon map doesn't know about
 *       fall back to a small dot — the row still renders, it just
 *       loses the glyph.
 *     - Collapsed brand shows the BICON mark from branding.json
 *       (LOGO.BICON → LOGO.ICON → LOGO.FAVICON fallback chain), so
 *       the rail head matches the favicon instead of showing a
 *       generic "M" tile.
 *
 *   Inputs unchanged so the rest of the app doesn't have to adapt:
 *   isOpen, onClose, brand, menu (catalog entries pre-filtered by
 *   role/feature in Sidebar), currentPath, isCollapsed, onToggleCollapse.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { SECTION_ORDER } from '../../hooks/useDynamicCatalog';

import { Button } from '../elements/Button.jsx';
import { Icon, ICON_GLYPHS } from '../elements/icon.element.jsx';
import useIsDesktop, { readIsDesktop } from '../../hooks/useIsDesktop.js';
const BRAND = 'var(--moksy-brand)';

// ── Icon set ─────────────────────────────────────────────────────────
// One registry: components/elements/icon.element.jsx. The private map that
// lived here (82 glyphs, 15 of them redrawings of registry names) was folded
// into it — a member saw three "document" shapes between sidebar, card and
// empty state. Catalog rows name a registry glyph; `iconFromPath` is only the
// fallback for dictionary-injected rows that carry none.


// Last-chance heuristic for items the catalog grew without us noticing
// (or dictionary-injected entries). Lets a brand-new admin-storage-foo
// row still get a sensible glyph from its path family.
const iconFromPath = (path = '') => {
  if (path.startsWith('/admin/payments')) return 'creditCard';
  if (path.startsWith('/admin/ai'))       return 'sparkles';
  if (path.startsWith('/admin/audit'))    return 'clipboardList';
  if (path.startsWith('/admin/events'))   return 'activity';
  if (path.startsWith('/admin/notif'))    return 'bell';
  if (path.startsWith('/admin/comm'))     return 'mail';
  if (path.startsWith('/admin/jobs'))     return 'cog';
  if (path.startsWith('/admin/storage'))  return 'database';
  if (path.startsWith('/admin/dict'))     return 'bookOpen';
  if (path.startsWith('/admin/registry')) return 'archive';
  // Every unmatched /admin/* page used to get the same cog, which says nothing;
  // no glyph is more honest than a misleading one (VD-070).
  if (path.startsWith('/admin'))          return null;
  if (path.startsWith('/billing'))        return 'creditCard';
  if (path.startsWith('/projects'))       return 'folder';
  if (path.startsWith('/requirements'))   return 'chat';
  if (path.startsWith('/reviews'))        return 'clipboardCheck';
  if (path.startsWith('/reviewer'))       return 'userCheck';
  if (path.startsWith('/downloads'))      return 'download';
  if (path.startsWith('/wallet'))         return 'wallet';
  if (path.startsWith('/ladder'))         return 'trendingUp';
  if (path.startsWith('/profile'))        return 'user';
  if (path === '/home')                   return 'home';
  return null;
};

// Every catalog row — top level, group, and leaf — declares an `icon` name
// (see useDynamicCatalog). The map used to live here keyed by catalog id,
// which meant the nav's shape and its glyphs were edited in two files and
// nested rows were simply never covered. `icon` is now the single source of
// truth; the path heuristic only catches dictionary-injected rows, which have
// no baseline entry to carry one.
const NavIcon = ({ item }) => {
  const name = (item?.icon && ICON_GLYPHS[item.icon]) ? item.icon : iconFromPath(item?.path);
  if (!name) return <span className="inline-block h-[18px] w-[18px]" aria-hidden="true" />;
  return <Icon name={name} className="h-[18px] w-[18px]" />;
};

// Some nav items share a pathname and differ only by query string —
// e.g. "Pillars" (/…/pillars) vs "Horizontal pillars"
// (/…/pillars?scope=horizontal). A plain pathname match would light up
// both (the first one wins), so we disambiguate by query. `currentPath`
// is the full path incl. `?search`. Rule: the pathname must match, every
// query param the item declares must be present with the same value in
// the URL, and the URL must not select a different variant via a
// disambiguator key (scope/templateCode) that the item omits. Incidental
// params on other pages (e.g. /projects?filter=active) are ignored, so
// their highlighting keeps the old pathname-only behaviour.
// Query keys that DISTINGUISH one nav row from another sharing its pathname.
//
// "All schemas"/"People", "All data"/"Frontend", "All journeys"/"Pipeline",
// "All templates"/"Journey templates", "All runs"/"Failed" — every one of them
// is the same pathname with a filter, and without the key listed here the
// unfiltered row stays lit while a category is open.
//
// This list fell behind FOUR times, once per section, each caught by looking
// at the screen. `navIcons.test.js` now fails if a nav path uses a query key
// that is not here, so the fifth time is a test failure instead.
// `apply` is not a filter — it is the suffix that stops the catalog collapsing
// "Apply" into "Reviewer dashboard", both of which are /reviewer. Same rule
// applies: it distinguishes two rows sharing a pathname, so it belongs here.
const DISAMBIGUATOR_KEYS = ['scope', 'templateCode', 'group', 'domain', 'kind', 'state', 'apply'];
const isPathActive = (itemPath, currentPath) => {
  if (typeof itemPath !== 'string' || typeof currentPath !== 'string') return false;
  const [ip, iq = ''] = itemPath.split('?');
  const [cp, cq = ''] = currentPath.split('?');
  if (ip !== cp) return false;
  const itemQ = new URLSearchParams(iq);
  const curQ = new URLSearchParams(cq);
  for (const [k, v] of itemQ) {
    if (curQ.get(k) !== v) return false;
  }
  for (const k of DISAMBIGUATOR_KEYS) {
    if (curQ.has(k) && !itemQ.has(k)) return false;
  }
  return true;
};

// True if `item` (or any of its nested children, however deep) is the active
// route — used to auto-open the containing section, to auto-expand every
// ancestor group down to the active leaf, and to tint a collapsed group that
// holds it. Module scope because NavNode (below) needs it too.
const containsActivePath = (item, path) => {
  if (isPathActive(item.path, path)) return true;
  if (Array.isArray(item.children)) return item.children.some((c) => containsActivePath(c, path));
  return false;
};

export const NavigationDrawer = ({
  isOpen,
  onClose,
  brand,
  menu,
  currentPath,
  // Reviewer accounts cannot own projects (the services enforce it), so the
  // "+ New" CTA is withheld from them rather than leading to a refusal.
  canStartProjects = true,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const closeButtonRef = useRef(null);
  const brandName = brand?.META?.NAME || brand?.NAME || 'Moksy';
  const brandLogo = brand?.LOGO?.PRIMARY || brand?.LOGO?.SECONDARY || '';
  // Collapsed rail head — prefer the dedicated icon mark, fall back to
  // the favicon URL, then to the existing letter tile if nothing is
  // configured. BICON in branding.json is "/images/branding/icon/
  // icon-black.png" (local), ICON / FAVICON are the live CDN URLs.
  const brandMark = brand?.LOGO?.BICON || brand?.LOGO?.ICON || brand?.LOGO?.FAVICON || '';
  const brandTagline = brand?.TAGLINE || 'Turn your idea into a build-ready plan';

  // Collapsing is a DESKTOP layout choice. The rail renders different children
  // — icons, no labels — and only its width class carried an `lg:` prefix, so
  // the mobile drawer was drawing the rail's innards at full width: a 260px
  // panel of unlabelled icons, in the state the app opens in by default.
  // Every "is this the rail?" question below asks this, not the raw prop.
  const isDesktop = useIsDesktop();
  const isRail = isCollapsed && isDesktop;

  const drawerRef = useFocusTrap({
    active: isOpen && !isDesktop,
    onEscape: onClose,
    initialFocusRef: closeButtonRef
  });

  // Group items by section, in catalog order. Items without a section
  // land in a final unlabelled bucket so dictionary-injected entries
  // don't get lost. Items whose path includes `:` placeholders aren't
  // navigable directly — they're skipped here but stay in the route
  // table for deep-link entry. Items carrying `children` (nested nav
  // groups, e.g. "Templates") have no `path` of their own — a group is
  // navigable-as-a-row regardless, since it's the toggle itself.
  const groups = useMemo(() => {
    const isNavigable = (p) => typeof p === 'string' && !p.includes(':');
    const UNGROUPED = '__UNGROUPED__';
    const map = new Map();
    for (const s of SECTION_ORDER) map.set(s, []);
    for (const item of (menu || [])) {
      if (!Array.isArray(item.children) && !isNavigable(item.path)) continue;
      const key = item.section || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    // Render in declared order, then any "extra" sections, then ungrouped.
    const orderedKeys = [
      ...SECTION_ORDER,
      ...[...map.keys()].filter((k) => !SECTION_ORDER.includes(k) && k !== UNGROUPED),
      UNGROUPED
    ];
    return orderedKeys
      .map((k) => ({ key: k, items: map.get(k) || [] }))
      .filter((g) => g.items.length > 0);
  }, [menu]);

  // Which section contains the active route — that's the one we
  // default-open. Falls back to the first group when nothing matches.
  const activeSection = useMemo(() => {
    for (const g of groups) {
      if (g.items.some((it) => containsActivePath(it, currentPath))) return g.key;
    }
    return groups[0]?.key || null;
  }, [groups, currentPath]);

  // Open/closed state per section. Initialised to "only the active
  // section open" so a fresh page load is scannable; users can
  // expand others manually and we remember it for the session.
  const [openSections, setOpenSections] = useState(() => ({}));
  useEffect(() => {
    setOpenSections((prev) => {
      // Make sure the active section is open whenever the route
      // changes. Don't collapse anything the user has already opened.
      if (!activeSection) return prev;
      if (prev[activeSection]) return prev;
      return { ...prev, [activeSection]: true };
    });
  }, [activeSection]);

  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  // Open/closed state per nested group node (id -> bool), independent
  // of section open state. Auto-expands every ancestor group of the
  // active route on navigation, same idea as activeSection above but
  // one level deeper — never auto-collapses a group the user opened.
  const [openNodes, setOpenNodes] = useState(() => ({}));
  const toggleNode = (id) => setOpenNodes((s) => ({ ...s, [id]: !s[id] }));
  useEffect(() => {
    const ids = [];
    const walk = (items) => {
      for (const item of items) {
        if (!Array.isArray(item.children)) continue;
        if (containsActivePath(item, currentPath)) {
          ids.push(item.id);
          walk(item.children);
        }
      }
    };
    walk(menu || []);
    if (!ids.length) return;
    setOpenNodes((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of ids) {
        if (!next[id]) { next[id] = true; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [menu, currentPath]);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen ? (
        <div
          className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px] lg:hidden"
          onClick={onClose}
        />
      ) : null}

      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal={isOpen ? 'true' : 'false'}
        aria-label="Primary navigation"
        className={`
          fixed inset-y-0 left-0 z-50 flex w-[85vw] max-w-[260px] transform flex-col bg-background border-r border-[var(--moksy-border)] transition-all duration-300 ease-in-out
          lg:static lg:translate-x-0 ${isCollapsed ? 'lg:w-[68px] lg:max-w-[68px]' : 'lg:w-[260px] lg:max-w-[260px]'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Edge-tab toggle (desktop only).
            `hidden lg:inline-flex`, both halves required: the rail opens
            collapsed by default, so without the lg half this is the only
            control that can widen it and the sidebar is stuck at 68px with
            no way out. It has to sit ON the border (-right-3) to read as a
            handle for the edge rather than a button floating in the nav. */}
        <Button variant="outline" size="icon" icon={false}
          className="absolute top-6 -right-3 z-10 hidden h-6 w-6 p-0 shadow-sm lg:inline-flex"
          onClick={onToggleCollapse}
          aria-expanded={isCollapsed ? 'false' : 'true'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <span className="text-[10px] leading-none" aria-hidden="true">
            {isCollapsed ? '›' : '‹'}
          </span>
        </Button>

        {/* Brand */}
        <div className={`flex h-16 items-center border-b border-[var(--moksy-divider)] ${isRail ? 'justify-center px-2' : 'justify-between px-4'}`}>
          <Link
            to="/"
            className={`flex items-center min-w-0 ${isRail ? 'justify-center' : 'gap-2.5'}`}
            onClick={onClose}
            title={brandName}
            aria-label={brandName}
          >
            {isRail ? (
              // Collapsed rail — use the favicon-style mark from
              // branding.json. The image gets `object-contain` so a
              // wider-than-square mark still fits the 36px tile.
              brandMark ? (
                <img
                  src={brandMark}
                  alt={brandName}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold text-white"
                  style={{ backgroundColor: BRAND }}
                >
                  {brandName.charAt(0)}
                </div>
              )
            ) : brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-7 w-auto max-w-[140px] object-contain object-left" />
            ) : (
              <>
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white"
                  style={{ backgroundColor: BRAND }}
                >
                  {brandName.charAt(0)}
                </div>
                <span className="text-base font-semibold text-[var(--moksy-text)]">{brandName}</span>
              </>
            )}
          </Link>
          {/* Mobile only, and NOT gated on isCollapsed: collapsing is a
              desktop concern (every collapse class below is lg:-prefixed) and
              the rail defaults to collapsed, so gating this hid the drawer's
              own close button on the phones that are the only place it shows.
              It is also the focus-trap's initial target — with it unrendered,
              opening the drawer focused nothing. */}
          <Button variant="ghost" pill={false} className="lg:hidden"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close navigation">
            <span className="text-lg leading-none" aria-hidden="true">×</span>
          </Button>
        </div>

        {/* "+ New" CTA — the only place the brand fill appears
            permanently in the sidebar. Routes to `/` which is the
            project-start surface (the landing page hosts the textarea
            + starter chips that kick off a new requirements
            conversation; works for both authenticated and guest
            users via the same code path). Mobile drawer auto-closes
            after the navigation so the user lands cleanly on the
            new-project page. */}
        {canStartProjects ? (
        <div className={`px-3 py-3 ${isRail ? 'flex justify-center' : ''}`}>
          {isRail ? (
            <Link
              to="/"
              title="New project"
              onClick={() => { if (!isDesktop) onClose?.(); }}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white"
              style={{ backgroundColor: BRAND }}
            >
              <span className="text-base leading-none" aria-hidden="true">+</span>
            </Link>
          ) : (
            <Link
              to="/"
              onClick={() => { if (!isDesktop) onClose?.(); }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-95"
              style={{ backgroundColor: BRAND }}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-background/20 text-xs font-bold">+</span>
              New project
            </Link>
          )}
        </div>
        ) : null}

        {/* Nav body */}
        <nav className={`flex-1 overflow-y-auto ${isRail ? 'px-2 py-1' : 'px-3 py-1'} space-y-1`}>
          {groups.map(({ key, items }) => {
            const sectionLabel = key === '__UNGROUPED__' ? '' : key;
            const isOpen = !!openSections[key];
            // In collapsed (rail) mode there's no room for nested groups —
            // flatten any nested children straight into the icon list,
            // dropping the group labels (matches the existing "no headers
            // in rail mode" behaviour for section labels).
            if (isRail) {
              // Section labels don't fit the rail, so a hairline stands in for
              // the header — without it a staff account's rail is one
              // undifferentiated column of ~37 icons.
              const isFirst = groups[0]?.key === key;
              return (
                <div key={key} className={`space-y-1 pb-1 ${isFirst ? '' : 'mt-1 pt-2 border-t border-[var(--moksy-divider)]'}`}>
                  {items.map((item) => {
                    const target = railTarget(item);
                    if (!target) return null;
                    return (
                      <SidebarItem
                        key={item.id || item.path}
                        item={target}
                        active={containsActivePath(item, currentPath)}
                        collapsed
                        onClick={onClose}
                      />
                    );
                  })}
                </div>
              );
            }
            return (
              <div key={key} className="pb-0.5">
                {sectionLabel ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(key)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9CA3AF] hover:text-[#374151]"
                    aria-expanded={isOpen ? 'true' : 'false'}
                  >
                    <span className="truncate">{sectionLabel}</span>
                    <span aria-hidden="true" className="text-[10px] text-[#D1D5DB]">{isOpen ? '▾' : '▸'}</span>
                  </button>
                ) : null}
                {isOpen || !sectionLabel ? (
                  <div className="space-y-0.5 mt-0.5">
                    {items.map((item) => (
                      <NavNode
                        key={item.id || item.path}
                        item={item}
                        currentPath={currentPath}
                        depth={0}
                        openNodes={openNodes}
                        onToggle={toggleNode}
                        onClick={onClose}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={`border-t border-[var(--moksy-divider)] ${isRail ? 'px-2 py-3 flex justify-center' : 'px-4 py-3'}`}>
          {isRail ? (
            <span title="All systems online" className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                All systems online
              </span>
            </div>
          )}
          {!isRail && brandTagline ? (
            <p className="mt-2 text-[10px] text-[#9CA3AF] leading-snug">{brandTagline}</p>
          ) : null}
        </div>
      </aside>
    </>
  );
};

// Collapsed (icon rail) mode has no room for labels, so it can't show nesting.
// It used to flatten every group down to its leaves — fine when the only group
// was Templates, unusable now that each admin service contributes 3–7 children
// (a super-admin's rail became ~140 unlabelled icons in a 68px column).
//
// The rail now shows ONE row per top-level item, which is the same set of
// concepts the expanded sidebar shows at rest. A group row links to its first
// descendant leaf — the closest thing to "open this group" when there's no room
// to expand — and lights up whenever the active route is anywhere inside it.
const firstLeaf = (item) => {
  if (!Array.isArray(item.children) || !item.children.length) return item;
  for (const child of item.children) {
    const leaf = firstLeaf(child);
    if (leaf?.path) return leaf;
  }
  return null;
};

// One rail row: the top-level item's own icon + label, pointing at itself or,
// for a group, at its first reachable leaf.
const railTarget = (item) => {
  const leaf = firstLeaf(item);
  if (!leaf?.path) return null;
  return { ...item, path: leaf.path };
};

// Recursive nav tree node. Leaf items (no `children`) render exactly
// like the pre-nesting flat SidebarItem. Group items (`children`
// present) render as a toggle row — label + chevron, no navigation of
// their own — and, when open, their children indented one level
// deeper, however many levels that recurses.
const NavNode = ({ item, currentPath, depth, openNodes, onToggle, onClick }) => {
  if (Array.isArray(item.children) && item.children.length) {
    const isOpen = !!openNodes[item.id];
    // A group row carries its own glyph, same slot and size as a leaf's, so
    // the icon column stays a straight line down the rail at every depth.
    // When the group is collapsed but holds the active route, tint the row —
    // otherwise navigating into a closed group loses every trace of where
    // you are. (Open groups don't tint: the active leaf is already visible.)
    const holdsActive = containsActivePath(item, currentPath);
    const showAsActive = holdsActive && !isOpen;
    return (
      <div>
        <button
          type="button"
          onClick={() => onToggle(item.id)}
          aria-expanded={isOpen ? 'true' : 'false'}
          style={{ paddingLeft: `${12 + depth * 14}px` }}
          className={`flex w-full items-center justify-between gap-2 rounded-lg py-1.5 pr-3 text-sm font-medium ${
            showAsActive
              ? 'bg-[var(--moksy-brand-tint)] text-[var(--moksy-brand-dark)]'
              : 'text-[#374151] hover:bg-[var(--moksy-soft)] hover:text-[var(--moksy-text)]'
          }`}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span
              className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                showAsActive ? 'text-[var(--moksy-brand-dark)]' : 'text-[#6B7280]'
              }`}
            >
              <NavIcon item={item} />
            </span>
            <span className="truncate">{item.label}</span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-[10px] text-[#9CA3AF]">{isOpen ? '▾' : '▸'}</span>
        </button>
        {isOpen ? (
          <div className="space-y-0.5">
            {item.children.map((child) => (
              <NavNode
                key={child.id || child.path}
                item={child}
                currentPath={currentPath}
                depth={depth + 1}
                openNodes={openNodes}
                onToggle={onToggle}
                onClick={onClick}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div style={{ paddingLeft: `${depth * 14}px` }}>
      <SidebarItem item={item} active={isPathActive(item.path, currentPath)} onClick={onClick} />
    </div>
  );
};

const SidebarItem = ({ item, active, collapsed, onClick }) => {
  // Read at click time rather than subscribing: this renders once per nav row,
  // and a media-query listener each would be dozens of them to answer a
  // question only a tap ever asks. Navigating closes the drawer on mobile,
  // where it covers the page; on desktop it is the page furniture and stays.
  const handleClick = () => {
    if (!readIsDesktop()) onClick?.();
  };
  if (collapsed) {
    return (
      <Link
        to={item.path}
        title={item.label}
        onClick={handleClick}
        aria-current={active ? 'page' : undefined}
        className={`flex items-center justify-center h-10 rounded-lg transition-colors ${
          active
            ? 'bg-[var(--moksy-brand-tint)] text-[var(--moksy-brand-dark)] ring-1 ring-[var(--moksy-brand-border)]'
            : 'text-[#6B7280] hover:bg-[var(--moksy-soft)] hover:text-[var(--moksy-text)]'
        }`}
      >
        <NavIcon item={item} />
      </Link>
    );
  }
  return (
    <Link
      to={item.path}
      onClick={handleClick}
      aria-current={active ? 'page' : undefined}
      className={`group flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-[var(--moksy-brand-tint)] text-[var(--moksy-brand-dark)]'
          : 'text-[#374151] hover:bg-[var(--moksy-soft)] hover:text-[var(--moksy-text)]'
      }`}
    >
      <span
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
          active
            ? 'text-[var(--moksy-brand-dark)]'
            : 'text-[#6B7280] group-hover:text-[var(--moksy-text)]'
        }`}
      >
        <NavIcon item={item} />
      </span>
      <span className="truncate">{item.label}</span>
      {/* What the row holds. Absent until the count has been fetched — a row
          that showed 0 while loading would read as an empty category, which is
          the one thing the number is there to rule out. */}
      {Number.isFinite(item.count) && (
        <span className="ml-auto shrink-0 text-xs tabular-nums text-[#9CA3AF]">{item.count}</span>
      )}
    </Link>
  );
};
